import React, { useCallback, useEffect, useRef, useState } from 'react';
import Drawer from 'react-modern-drawer';
import { useAuth } from '../../../../modules/auth';
import { useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import db from '../../../../../services/firebase';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';
import DrawerHeader from '../drawer-components/DrawerHeader';
import { DrawerContent } from '../drawer-components/DrawerContent';
import Overlay from 'app/components/Overlay';
import DrawerFooter from '../drawer-components/DrawerFooter';
import InfoCard from '../drawer-components/InfoCard';
import { AddDrawerIntegrationProps } from '../utils/types';

const GCPDrawer: React.FC<AddDrawerIntegrationProps> = ({ id, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(false);
  const [openAccordion2, setOpenAccordion2] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const drawerTopRef = useRef<HTMLDivElement>(null);
  const [integration, setIntegration] = useState<any[]>([]);
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected';

  const getIntegration = useCallback(() => {
    try { 
      const tenantId = currentUser?.tenantId || '';
      if (!id) return;
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.name === 'Google Cloud Platform') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              projectId: data.projectId,
              cloudName: data.cloudName,
            };
            setIntegration([integrationData]);
          }
        }
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error getting integration:', error);
    }
  }, [currentUser?.tenantId, id]);

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
  }, [id, isOpen, getIntegration]);

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showAlert]);

  const handleConnect = async () => {
    setIsLoading(true);
    try {
      navigate('/vulnerabilities/clouds/gcp/configuration');
      handleClose();
    } catch (error) {
      console.error('Error connecting to GCP:', error);
      showErrorToast({ message: 'Failed to connect to Google Cloud Platform' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowAlert(false);
    onClose();
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      const tenantId = currentUser?.tenantId || '';
      if (!id || !tenantId) return;
      const tenantRef = doc(db, 'tenants', tenantId);

      const assetsRef = collection(tenantRef, 'assets');
      const gcpAssetsQuery = query(assetsRef, where('platform', '==', 'Google Cloud Platform'));
      const gcpAssetsSnapshot = await getDocs(gcpAssetsQuery);

      const deleteAssetPromises = gcpAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id));
      });

      await Promise.all(deleteAssetPromises);

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id);
      await deleteDoc(integrationRef);

      const vendorsRef = collection(tenantRef, 'vendors');
      const gcpVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Google Cloud Platform'));
      const gcpVendorSnapshot = await getDocs(gcpVendorQuery);

      const deleteVendorPromises = gcpVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id));
      });

      await Promise.all(deleteVendorPromises);

      const activitiesRef = collection(tenantRef, 'activities');
      const currentDate = new Date();
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`;
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Google Cloud Platform integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      });

      showSuccessToast({ message: 'Google Cloud Platform integration disconnected' });

      setIntegration([]);
      handleClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error disconnecting Google Cloud Platform:', errorMessage);
      showErrorToast({ message: 'Failed to disconnect Google Cloud Platform' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} direction="right" className="" size="45%">
      <DrawerHeader title="Google Cloud Platform" onClose={onClose} />

      <div
        className="scroll-y overflow-auto p-6 mb-10"
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className="form">
          <DrawerContent
            title="Google Cloud Platform"
            description="Cloud Provider"
            logo="/integrations/gcp.svg"
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo="SecureSlate will be granted permissions to scan your Google Cloud infrastructure for security vulnerabilities 
            and compliance issues respecting IAM policies and service account permissions."
            accessTypeinfo="Requires a Google Cloud project with appropriate APIs enabled and a service account with necessary permissions for vulnerability scanning and security assessment."
          />
          <>
            <div className="mb-8 d-flex justify-content-between align-items-center">
              <div className="fs-6 fw-bold text-bold">
                {isConnected ? 'Google Cloud Platform Connected to SecureSlate' : 'Connect Google Cloud Platform to SecureSlate'}{' '}
                <Overlay
                  content="Upon successful connection, SecureSlate will scan your Google Cloud infrastructure for security 
                  vulnerabilities and compliance issues. All findings will be automatically imported and presented within the 
                  Vulnerability Management section for review and remediation."
                />
              </div>
            </div>
            <InfoCard
              type="info"
              title="Connect Google Cloud Platform to SecureSlate"
              description="Click on the 'Connect' button below. You will be guided through a 5-step process to configure your Google Cloud Platform integration for vulnerability scanning. This includes project setup, API enablement, service account creation, and access key configuration to enable comprehensive security assessment of your cloud infrastructure."
            />
          </>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  );
};

export default GCPDrawer;
